import { IInboxConversationTagSetting } from './inboxSettingsStore'

/** Settings keys → AI `conversationTags.label` values used for Main inbox routing. */
export const MAIN_INBOX_AI_TAG_LABEL_BY_SETTING_KEY: Record<string, string> = {
    inbox_conversation_tag_awaiting_reply: 'Awaiting reply',
    inbox_conversation_tag_to_respond: 'To respond',
    inbox_conversation_tag_warm_intro: 'Warm intro',
    inbox_conversation_tag_fyi: 'FYI',
    inbox_conversation_tag_actioned: 'Actioned',
    inbox_conversation_tag_marketing: 'Marketing',
}

export const INBOX_CONVERSATION_TAG_DEFAULTS: IInboxConversationTagSetting[] = [
    {
        key: 'inbox_keep_in_main',
        title: 'Keep in Main inbox',
        description:
            'Toggle on to keep conversations auto-tagged with this label in your Main Inbox. Toggle off to route them to the All Messages inbox and its matching Conversation Tag.',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_sales_navigator',
        title: 'Sales Navigator',
        description: 'Paid InMail messages from Sales Navigator, Recruiter, or open InMail credits.',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_new_connection',
        title: 'New Connection',
        description:
            'The first 7 days of chat with a new connection, including the note from their connection request.',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_snoozed',
        title: 'Snoozed',
        description:
            'Messages you set aside for later. They resurface at the top of your inbox at the scheduled time, highlighted.',
        defaultValue: false,
    },
    {
        key: 'inbox_conversation_tag_awaiting_reply',
        title: 'Awaiting reply',
        description: `You sent the last message and they haven't replied yet.`,
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_to_respond',
        title: 'To Respond',
        description:
            'They sent the last message and it needs your response. Excludes Promotion and Cold pitch threads.',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_warm_intro',
        title: 'Warm Intro',
        description:
            'Group messages with multiple recipients, or threads where you or they requested an introduction. Excludes Promotion and Cold pitch threads.',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_fyi',
        title: 'FYI',
        description:
            'Messages or posts someone forwarded to you with no direct ask attached. Auto-replies indicating the sender is away (OOO).',
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_actioned',
        title: 'Actioned',
        description: `Threads you've resolved or closed. Hidden from your main inbox but always searchable.`,
        defaultValue: true,
    },
    {
        key: 'inbox_conversation_tag_marketing',
        title: 'Marketing',
        description:
            `LinkedIn ads, sponsored messages, and system-generated notifications. Unsolicited long-form outreach from someone selling to you, plus persistent follow-ups you've never responded to.`,
        defaultValue: false,
    },
]

/** Maps inbox filter tab ids to Settings toggle keys (scope tabs are omitted — always visible). */
export const INBOX_CONVERSATION_TAG_TAB_SETTINGS_KEYS: Record<string, string> = {
    awaitingReply: 'inbox_conversation_tag_awaiting_reply',
    'ai:To respond': 'inbox_conversation_tag_to_respond',
    snoozed: 'inbox_conversation_tag_snoozed',
    newConnection: 'inbox_conversation_tag_new_connection',
    'ai:Warm intro': 'inbox_conversation_tag_warm_intro',
    salesNav: 'inbox_conversation_tag_sales_navigator',
    'ai:FYI': 'inbox_conversation_tag_fyi',
    'ai:Actioned': 'inbox_conversation_tag_actioned',
    'ai:Marketing': 'inbox_conversation_tag_marketing',
}

/** Legacy localStorage keys from the former "Labels" settings. */
export const LEGACY_INBOX_LABEL_SETTING_KEYS: Record<string, string> = {
    inbox_label_sales_navigator: 'inbox_conversation_tag_sales_navigator',
    inbox_label_new_connection: 'inbox_conversation_tag_new_connection',
    inbox_label_snoozed: 'inbox_conversation_tag_snoozed',
    inbox_label_awaiting_reply: 'inbox_conversation_tag_awaiting_reply',
    inbox_label_to_respond: 'inbox_conversation_tag_to_respond',
    inbox_label_warm_intro: 'inbox_conversation_tag_warm_intro',
    inbox_label_fyi: 'inbox_conversation_tag_fyi',
    inbox_label_actioned: 'inbox_conversation_tag_actioned',
    inbox_label_marketing: 'inbox_conversation_tag_marketing',
}

/** Inbox tab id → AI `conversationTags.label` value shown on that tab. */
export const INBOX_TAB_TO_CONVERSATION_TAG_LABEL: Record<string, string> = {
    awaitingReply: 'Awaiting reply',
    'ai:To respond': 'To respond',
    'ai:Warm intro': 'Warm intro',
    'ai:FYI': 'FYI',
    'ai:Actioned': 'Actioned',
    'ai:Marketing': 'Marketing',
}

export const isConversationTagInboxTab = (label: string | undefined): boolean =>
    !!label && (label.startsWith('ai:') || label === 'awaitingReply')

/** Primary AI `conversationTags.label`, if any. */
export const getConversationTagLabel = (
    conversationTags: Array<{ label?: string }> | undefined | null,
): string | null => conversationTags?.find((t) => t?.label)?.label ?? null

/** True when relabel is still in-flight (same label as before send, or empty after clear). */
export const isStaleRelabelTagStatus = (
    beforeLabel: string | null | undefined,
    row: { conversationTags?: Array<{ label?: string }>; conversationTagPending?: boolean },
): boolean => {
    if (row.conversationTagPending) return true

    const afterLabel = getConversationTagLabel(row.conversationTags)
    if (afterLabel === beforeLabel) return true

    // BE cleared tags for relabeling but AI has not assigned a new label yet.
    if (beforeLabel != null && !afterLabel && !(row.conversationTags?.length)) {
        return true
    }

    return false
}

/** Client-side check whether a conversation still belongs on the active inbox tab. */
export const conversationMatchesInboxLabel = (conversation: any, label: string | undefined): boolean => {
    const isSnoozed = typeof conversation?.snoozedUntil === 'number' && conversation.snoozedUntil > Date.now()
    if (label === 'snoozed') return isSnoozed
    if (isSnoozed) return false
    if (!label || label === 'allMessages' || label === 'mainInbox') return true
    if (label === 'salesNav') return !!conversation?.isSales
    if (label === 'newConnection') return true

    const wantedLabel =
        INBOX_TAB_TO_CONVERSATION_TAG_LABEL[label] ??
        (label.startsWith('ai:') ? label.slice(3) : null)
    if (!wantedLabel) return true

    return (
        conversation?.conversationTags?.some(
            (tag: { label?: string }) => tag?.label === wantedLabel,
        ) ?? false
    )
}

/** AI tag labels the user enabled for Main inbox (from Settings toggles). */
export const getMainInboxEnabledAiTagLabels = (
    conversationTagSettings: Record<string, boolean>,
): string[] =>
    Object.entries(MAIN_INBOX_AI_TAG_LABEL_BY_SETTING_KEY)
        .filter(([key]) => conversationTagSettings[key] !== false)
        .map(([, label]) => label)
